/**
 * Mock data for categories
 *
 * Mock-данные категорий.
 * В будущем будут загружаться динамически с сервера.
 *
 * ВАЖНО: Используется новая структура Category из shared-types
 * - Единая структура для категорий и подкатегорий
 * - parentId = null для корневых категорий
 * - parentId = id родителя для подкатегорий
 * - Обязательные поля: slug, nameEn, nameFr
 * - imageUrl у корневых категорий ссылается на обложку в /public/images/categories
 */

import type { Category } from '@hummii/shared-types'

/**
 * All categories array (flat structure)
 * Root categories have parentId = null
 * Subcategories have parentId = parent category id
 */
export const mockCategories: Category[] = [
  // ============================================================================
  // ROOT CATEGORIES
  // ============================================================================

  // Category 1: Handyman & Home Services
  {
    id: '1',
    slug: 'handyman-home-services',
    nameEn: 'Handyman & Home Services',
    nameFr: 'Bricolage et services à domicile',
    descriptionEn: 'Furniture assembly, mounting, installations, and home repairs',
    descriptionFr: 'Assemblage de meubles, installations et réparations domiciliaires',
    icon: 'handyman',
    imageUrl: '/images/categories/handyman-home-services.png',
    parentId: null,
    isActive: true,
    isPopular: true,
    sortOrder: 1,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // Category 2: Home Cleaning
  {
    id: '2',
    slug: 'home-cleaning',
    nameEn: 'Home Cleaning',
    nameFr: 'Nettoyage résidentiel',
    descriptionEn: 'Residential, deep, move-in/out, and specialty cleaning services',
    descriptionFr: 'Nettoyage résidentiel, en profondeur et spécialisé',
    icon: 'cleaning',
    imageUrl: '/images/categories/home-cleaning.png',
    parentId: null,
    isActive: true,
    isPopular: true,
    sortOrder: 2,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // Category 3: Moving & Delivery
  {
    id: '3',
    slug: 'moving-delivery',
    nameEn: 'Moving & Delivery',
    nameFr: 'Déménagement et livraison',
    descriptionEn: 'Moving help, heavy lifting, junk removal, and delivery services',
    descriptionFr: 'Aide au déménagement, levage, enlèvement de déchets et livraison',
    icon: 'moving',
    imageUrl: '/images/categories/moving-delivery.png',
    parentId: null,
    isActive: true,
    isPopular: true,
    sortOrder: 3,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // Category 4: Renovation & Trades
  {
    id: '4',
    slug: 'renovation-trades',
    nameEn: 'Renovation & Trades',
    nameFr: 'Rénovation et métiers',
    descriptionEn: 'Painting, drywall, flooring, carpentry, and general renovations',
    descriptionFr: 'Peinture, cloisons sèches, revêtement de sol, menuiserie et rénovations',
    icon: 'renovation',
    imageUrl: '/images/categories/renovation-trades.png',
    parentId: null,
    isActive: true,
    isPopular: true,
    sortOrder: 4,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // Category 5: Yard & Seasonal Services
  {
    id: '5',
    slug: 'yard-seasonal-services',
    nameEn: 'Yard & Seasonal Services',
    nameFr: 'Cour et services saisonniers',
    descriptionEn: 'Lawn care, leaf and snow removal, gutter cleaning, and gardening',
    descriptionFr: 'Entretien de pelouse, déneigement, nettoyage de gouttières et jardinage',
    icon: 'yard',
    imageUrl: '/images/categories/yard-seasonal-services.png',
    parentId: null,
    isActive: true,
    isPopular: true,
    sortOrder: 5,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // ============================================================================
  // SUBCATEGORIES
  // ============================================================================

  // Handyman & Home Services - Subcategories
  {
    id: '1-1',
    slug: 'furniture-assembly',
    nameEn: 'Furniture Assembly',
    nameFr: 'Assemblage de meubles',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '1',
    isActive: true,
    isPopular: true,
    sortOrder: 1,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '1-2',
    slug: 'tv-mounting',
    nameEn: 'TV Mounting',
    nameFr: 'Installation de téléviseur',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '1',
    isActive: true,
    isPopular: false,
    sortOrder: 2,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '1-3',
    slug: 'shelf-picture-hanging',
    nameEn: 'Shelf & Picture Hanging',
    nameFr: 'Pose d\'étagères et de cadres',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '1',
    isActive: true,
    isPopular: false,
    sortOrder: 3,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '1-4',
    slug: 'appliance-installation',
    nameEn: 'Appliance Installation',
    nameFr: 'Installation d\'appareils électroménagers',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '1',
    isActive: true,
    isPopular: false,
    sortOrder: 4,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '1-5',
    slug: 'minor-home-repairs',
    nameEn: 'Minor Home Repairs',
    nameFr: 'Petites réparations domiciliaires',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '1',
    isActive: true,
    isPopular: true,
    sortOrder: 5,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '1-6',
    slug: 'smart-home-setup',
    nameEn: 'Smart Home Setup',
    nameFr: 'Configuration de maison intelligente',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '1',
    isActive: true,
    isPopular: false,
    sortOrder: 6,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '1-7',
    slug: 'general-handyman',
    nameEn: 'General Handyman',
    nameFr: 'Bricolage général',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '1',
    isActive: true,
    isPopular: true,
    sortOrder: 7,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // Home Cleaning - Subcategories
  {
    id: '2-1',
    slug: 'house-cleaning',
    nameEn: 'House Cleaning',
    nameFr: 'Nettoyage de maison',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '2',
    isActive: true,
    isPopular: true,
    sortOrder: 1,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '2-2',
    slug: 'deep-cleaning',
    nameEn: 'Deep Cleaning',
    nameFr: 'Nettoyage en profondeur',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '2',
    isActive: true,
    isPopular: true,
    sortOrder: 2,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '2-3',
    slug: 'move-in-move-out-cleaning',
    nameEn: 'Move-In / Move-Out Cleaning',
    nameFr: 'Nettoyage d\'emménagement / déménagement',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '2',
    isActive: true,
    isPopular: false,
    sortOrder: 3,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '2-4',
    slug: 'window-cleaning',
    nameEn: 'Window Cleaning',
    nameFr: 'Nettoyage de vitres',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '2',
    isActive: true,
    isPopular: false,
    sortOrder: 4,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '2-5',
    slug: 'airbnb-cleaning',
    nameEn: 'Airbnb Cleaning',
    nameFr: 'Nettoyage Airbnb',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '2',
    isActive: true,
    isPopular: false,
    sortOrder: 5,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '2-6',
    slug: 'office-cleaning',
    nameEn: 'Office Cleaning',
    nameFr: 'Nettoyage de bureaux',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '2',
    isActive: true,
    isPopular: false,
    sortOrder: 6,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '2-7',
    slug: 'garage-cleaning',
    nameEn: 'Garage Cleaning',
    nameFr: 'Nettoyage de garage',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '2',
    isActive: true,
    isPopular: false,
    sortOrder: 7,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // Moving & Delivery - Subcategories
  {
    id: '3-1',
    slug: 'help-moving',
    nameEn: 'Help Moving',
    nameFr: 'Aide au déménagement',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '3',
    isActive: true,
    isPopular: true,
    sortOrder: 1,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '3-2',
    slug: 'heavy-lifting',
    nameEn: 'Heavy Lifting',
    nameFr: 'Levage de charges lourdes',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '3',
    isActive: true,
    isPopular: false,
    sortOrder: 2,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '3-3',
    slug: 'loading-unloading',
    nameEn: 'Loading & Unloading',
    nameFr: 'Chargement et déchargement',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '3',
    isActive: true,
    isPopular: false,
    sortOrder: 3,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '3-4',
    slug: 'store-pickup-delivery',
    nameEn: 'Store Pickup & Delivery',
    nameFr: 'Ramassage et livraison en magasin',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '3',
    isActive: true,
    isPopular: false,
    sortOrder: 4,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '3-5',
    slug: 'junk-removal',
    nameEn: 'Junk Removal',
    nameFr: 'Enlèvement de déchets',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '3',
    isActive: true,
    isPopular: true,
    sortOrder: 5,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '3-6',
    slug: 'move-in-move-out-help',
    nameEn: 'Move-In / Move-Out Help',
    nameFr: 'Aide à l\'emménagement / déménagement',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '3',
    isActive: true,
    isPopular: false,
    sortOrder: 6,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '3-7',
    slug: 'large-item-delivery',
    nameEn: 'Large Item Delivery',
    nameFr: 'Livraison d\'articles volumineux',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '3',
    isActive: true,
    isPopular: false,
    sortOrder: 7,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // Renovation & Trades - Subcategories
  {
    id: '4-1',
    slug: 'interior-exterior-painting',
    nameEn: 'Interior & Exterior Painting',
    nameFr: 'Peinture intérieure et extérieure',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '4',
    isActive: true,
    isPopular: true,
    sortOrder: 1,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '4-2',
    slug: 'drywall-repair',
    nameEn: 'Drywall Repair',
    nameFr: 'Réparation de cloisons sèches',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '4',
    isActive: true,
    isPopular: false,
    sortOrder: 2,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '4-3',
    slug: 'flooring',
    nameEn: 'Flooring',
    nameFr: 'Revêtement de sol',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '4',
    isActive: true,
    isPopular: true,
    sortOrder: 3,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '4-4',
    slug: 'tile-work',
    nameEn: 'Tile Work',
    nameFr: 'Pose de carreaux',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '4',
    isActive: true,
    isPopular: false,
    sortOrder: 4,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '4-5',
    slug: 'carpentry',
    nameEn: 'Carpentry',
    nameFr: 'Menuiserie',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '4',
    isActive: true,
    isPopular: false,
    sortOrder: 5,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '4-6',
    slug: 'deck-repair',
    nameEn: 'Deck Repair',
    nameFr: 'Réparation de terrasse',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '4',
    isActive: true,
    isPopular: false,
    sortOrder: 6,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '4-7',
    slug: 'fence-repair',
    nameEn: 'Fence Repair',
    nameFr: 'Réparation de clôture',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '4',
    isActive: true,
    isPopular: false,
    sortOrder: 7,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '4-8',
    slug: 'general-renovations',
    nameEn: 'General Renovations',
    nameFr: 'Rénovations générales',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '4',
    isActive: true,
    isPopular: true,
    sortOrder: 8,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },

  // Yard & Seasonal Services - Subcategories
  {
    id: '5-1',
    slug: 'lawn-mowing',
    nameEn: 'Lawn Mowing',
    nameFr: 'Tonte de pelouse',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '5',
    isActive: true,
    isPopular: true,
    sortOrder: 1,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '5-2',
    slug: 'yard-cleanup',
    nameEn: 'Yard Cleanup',
    nameFr: 'Nettoyage de cour',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '5',
    isActive: true,
    isPopular: false,
    sortOrder: 2,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '5-3',
    slug: 'leaf-removal',
    nameEn: 'Leaf Removal',
    nameFr: 'Ramassage de feuilles',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '5',
    isActive: true,
    isPopular: false,
    sortOrder: 3,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '5-4',
    slug: 'snow-removal',
    nameEn: 'Snow Removal',
    nameFr: 'Déneigement',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '5',
    isActive: true,
    isPopular: true,
    sortOrder: 4,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '5-5',
    slug: 'gutter-cleaning',
    nameEn: 'Gutter Cleaning',
    nameFr: 'Nettoyage de gouttières',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '5',
    isActive: true,
    isPopular: false,
    sortOrder: 5,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '5-6',
    slug: 'pressure-washing',
    nameEn: 'Pressure Washing',
    nameFr: 'Lavage à pression',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '5',
    isActive: true,
    isPopular: false,
    sortOrder: 6,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '5-7',
    slug: 'gardening-weeding',
    nameEn: 'Gardening & Weeding',
    nameFr: 'Jardinage et désherbage',
    descriptionEn: null,
    descriptionFr: null,
    icon: null,
    imageUrl: null,
    parentId: '5',
    isActive: true,
    isPopular: false,
    sortOrder: 7,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/** Get category by ID */
export const getCategoryById = (id: string): Category | undefined => {
  return mockCategories.find((cat) => cat.id === id);
};

/** Get category by slug */
export const getCategoryBySlug = (slug: string): Category | undefined => {
  return mockCategories.find((cat) => cat.slug === slug);
};

/** Get all root categories (parentId = null) */
export const getRootCategories = (): Category[] => {
  return mockCategories.filter((cat) => cat.parentId === null);
};

/** Get all subcategories for a given parent category ID */
export const getSubcategories = (parentId: string): Category[] => {
  return mockCategories.filter((cat) => cat.parentId === parentId);
};
